import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import CryptoJS from "crypto-js";
import type { IneligibleItem, CustomerGets } from "@/types";

export interface DiscountDetail {
  code: string;
  brandId?: string; // marketplace: scoped to a specific brand/retailer
  discountValue: number; // e.g. 0.1 for 10%, 150 for R150 flat
  totalValue: number; // in cents - the actual amount off
  discountsOn: string[]; // cart_id's the discount applies to
  type: string; // DiscountType value
  ineligibleItems?: IneligibleItem[];
  customerGets?: CustomerGets[];
  codeType?: string; // "DiscountPercentage" | "DiscountAmount"
  isShippingDiscount?: boolean;
}

interface DiscountStore {
  totalDiscountDetail: DiscountDetail[];
  addDiscount: (discount: DiscountDetail) => void;
  removeDiscount: (code: string, brandId?: string) => void;
  removeDiscountsByCode: (code: string) => void;
  keepDiscounts: (codes: string[]) => void;
  updateDiscount: (code: string, updates: Partial<DiscountDetail>, brandId?: string) => void;
  clearAll: () => void;
}

const ENCRYPTION_KEY = "nile-checkout-discount-key";

const encryptedStorage = {
  getItem: (name: string): string | null => {
    const encrypted = localStorage.getItem(name);
    if (!encrypted) return null;
    try {
      const decrypted = CryptoJS.AES.decrypt(encrypted, ENCRYPTION_KEY);
      return decrypted.toString(CryptoJS.enc.Utf8);
    } catch {
      return null;
    }
  },
  setItem: (name: string, value: string): void => {
    const encrypted = CryptoJS.AES.encrypt(value, ENCRYPTION_KEY).toString();
    localStorage.setItem(name, encrypted);
  },
  removeItem: (name: string): void => {
    localStorage.removeItem(name);
  },
};

export const useDiscountStore = create<DiscountStore>()(
  persist(
    (set) => ({
      totalDiscountDetail: [],
      addDiscount: (discount) =>
        set((state) => ({
          totalDiscountDetail: [
            ...state.totalDiscountDetail.filter(
              (d) => !(d.code === discount.code && d.brandId === discount.brandId),
            ),
            discount,
          ],
        })),
      removeDiscount: (code, brandId) =>
        set((state) => ({
          totalDiscountDetail: state.totalDiscountDetail.filter(
            (d) => !(d.code === code && d.brandId === brandId),
          ),
        })),
      removeDiscountsByCode: (code) =>
        set((state) => ({
          totalDiscountDetail: state.totalDiscountDetail.filter(
            (d) => d.code !== code,
          ),
        })),
      keepDiscounts: (codes) =>
        set((state) => ({
          totalDiscountDetail: state.totalDiscountDetail.filter(
            (d) => codes.includes(d.code),
          ),
        })),
      updateDiscount: (code, updates, brandId) =>
        set((state) => ({
          totalDiscountDetail: state.totalDiscountDetail.map((d) =>
            d.code === code && d.brandId === brandId ? { ...d, ...updates } : d,
          ),
        })),
      clearAll: () => set({ totalDiscountDetail: [] }),
    }),
    {
      name: "discount-store",
      storage: createJSONStorage(() => encryptedStorage),
    },
  ),
);
